import React, { useCallback, useMemo, useState } from 'react';
import {
  StyleSheet,
  Platform,
} from 'react-native';
import { showAlert } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { IBulletin, IBulletinTableRow } from '@/types/admin/bulletins';
import { ADMIN_COLORS } from '@/constants/admin';
import { 
  useEditBulletinV2Mutation, 
  convertToFormData 
} from '@/store/api/admin/bulletinsApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { isNetworkError } from '@/utils/admin/errorHandling';
import { useEditBulletinForm } from '@/components/admin/bulletins/hooks/useEditBulletinForm';
import { useDatePicker } from '@/components/admin/events/CreateEvent/hooks/useDatePicker';
import { DateTimePickerModal, PhotoUploadSection } from '@/components';
import { BulletinDateTimePickers } from '@/components/admin/bulletins/BulletinDateTimePickers';
import { 
  BulletinDetailsSection, 
  RecipientOptionsSection, 
  UrgencyOptionsSection, 
  PublishOptionsSection 
} from '@/components/admin/bulletins/CreateBulletin/components';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type EditBulletinScreenProps = StackScreenProps<AdminStackParamList, 'EditBulletin'>;

export const EditBulletinScreen: React.FC<EditBulletinScreenProps> = ({
  navigation,
  route,
}) => {
  const { layoutMode } = useResponsive();
  const { bulletin: bulletinTableRow } = route.params;
  const { showSuccess, showLoading, hideLoading, showWarning } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const [editBulletin, { isLoading: isSubmitting }] = useEditBulletinV2Mutation();

  // Convert IBulletinTableRow to IBulletin format (matching admin-web pattern)
  const convertTableRowToBulletin = useCallback((tableRow: IBulletinTableRow): IBulletin => {
    // Determine the original publish type and date
    const originalPublishType = tableRow.publishType as 'publishNow' | 'draft' | 'scheduled' || 'draft';
    const originalPublishDate = tableRow.date?.toISOString() || null;
    
    return {
      id: tableRow.id,
      title: tableRow.title,
      message: tableRow.description || '', // Use description as message content
      description: tableRow.description || '',
      propertyId: selectedPropertyId || 0,
      publishType: originalPublishType, // Preserve original publishType
      publishDate: originalPublishDate, // Preserve original publishDate
      highlight: tableRow.highlight,
      sendEmail: false, // Default value, will be set by form
      pinHomepage: tableRow.pinHomepage,
      recipientType: (tableRow.recipientType as 'all' | 'current' | 'future' | 'notice') || 'all',
      photos: [],
      createdAt: tableRow.date?.toISOString() || null,
      updatedAt: null,
      messageSentCount: tableRow.messageSentCount,
      messageReadCount: tableRow.messageReadCount,
      pushNotificationSentCount: tableRow.pushNotificationSentCount,
      files: tableRow.files || [],
      author: (tableRow.author || undefined) as any,
    };
  }, [selectedPropertyId]);

  const bulletinData = useMemo(() => convertTableRowToBulletin(bulletinTableRow), [bulletinTableRow, convertTableRowToBulletin]);

  // Custom hook for form logic with initial bulletin data
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    appendPhotos,
    showDiscardAlert,
    hasChanges,
  } = useEditBulletinForm(bulletinData);

  // Custom hook for date picker logic
  const {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showPublishDatePicker,
    hideDatePickers,
    confirmDateSelection,
    showFallbackDatePicker,
    setDatePickerMode,
  } = useDatePicker();

  // Web-only anchor state for dropdown positioning (match Events implementation)
  const [webAnchor, setWebAnchor] = useState<AnchorRect | null>(null);

  // Memoized date selection handler to prevent unnecessary re-renders
  const handleDateSelection = useCallback((date: Date, type: 'start' | 'end' | 'publish') => {
    if (type === 'publish') {
      handleFieldChange('publishTime', date);
      // Also update publishDate as ISO string for API
      handleFieldChange('publishDate', date.toISOString());
    }
  }, [handleFieldChange]);

  // Date picker handler with fallback support
  const handlePublishTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showPublishDatePicker(formData.publishTime || new Date());
      return;
    }
    if (DateTimePicker) {
      showPublishDatePicker(formData.publishTime || new Date());
    } else {
      showFallbackDatePicker('Publish Time', formData.publishTime || new Date(), (date) => {
        handleDateSelection(date, 'publish');
      });
    }
  }, [DateTimePicker, formData.publishTime, showPublishDatePicker, showFallbackDatePicker, handleDateSelection]);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    if (!bulletinData) {
      showAlert('Error', 'Bulletin data not loaded');
      return;
    }

    const loadingId = showLoading('Updating bulletin...');
    try {
      // Convert form data to the format expected by the API (include id for editing)
      const bulletinUpdateData = {
        id: bulletinTableRow.id, // Include the bulletin ID for PATCH request
        title: formData.title,
        message: formData.message,
        propertyId: selectedPropertyId,
        publishType: formData.publishType,
        publishDate: formData.publishTime?.toISOString() || formData.publishDate,
        highlight: formData.highlight,
        sendEmail: formData.sendEmail,
        pinHomepage: formData.pinHomepage,
        recipientType: formData.recipientType,
        photos: formData.photos || [],
      };

      const formDataToSend = convertToFormData(bulletinUpdateData, bulletinTableRow.id);
      const result = await editBulletin(formDataToSend).unwrap();
      
      hideLoading(loadingId);
      // Create more specific success messages based on the original and new publish types
      const originalPublishType = bulletinData.publishType;
      let successMessage = 'Bulletin updated successfully!';
      
      if (formData.publishType === 'draft') {
        successMessage = originalPublishType === 'scheduled' 
          ? 'Bulletin moved to drafts successfully!' 
          : 'Bulletin draft updated successfully!';
      } else if (formData.publishType === 'scheduled') {
        successMessage = originalPublishType === 'scheduled' 
          ? 'Bulletin schedule updated successfully!' 
          : 'Bulletin scheduled successfully!';
      } else if (formData.publishType === 'publishNow') {
        successMessage = originalPublishType === 'scheduled' 
          ? 'Bulletin published immediately!' 
          : 'Bulletin published successfully!';
      }
      
      // Check for partial success (bulletin updated but photos failed to upload)
      if (result.warning) {
        showWarning(`${successMessage} However, ${result.warning}`, {
          duration: 6000, // Longer duration for important warning
        });
      } else {
        showSuccess(successMessage);
      }
      navigation.goBack();
    } catch (error: any) {
      hideLoading(loadingId);
      console.error('Failed to update bulletin:', error);
      
      // Improved error message for network issues
      if (isNetworkError(error)) {
        showApiError(error, 'Network error. Please check your connection and try again.');
      } else {
        showApiError(error, 'Failed to update bulletin');
      }
    }
  }, [
    validateForm,
    selectedPropertyId,
    bulletinData,
    formData,
    bulletinTableRow.id,
    editBulletin,
    showSuccess,
    showWarning,
    showLoading,
    hideLoading,
    showApiError,
    navigation,
  ]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (Platform.OS === 'web') {
      // DesktopFormPage handles web discard confirmation; just navigate back
      navigation.goBack();
      return;
    }
    if (hasChanges()) {
      showDiscardAlert(() => navigation.goBack());
    } else {
      navigation.goBack();
    }
  }, [hasChanges, showDiscardAlert, navigation]);

  // Date picker confirmation handler
  const handleDatePickerConfirm = useCallback((pickerType: 'start' | 'end' | 'publish') => {
    confirmDateSelection(pickerType, handleDateSelection);
  }, [confirmDateSelection, handleDateSelection]);



  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <BulletinDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <PhotoUploadSection
        title="Bulletin Photos"
        subtitle="Add up to 10 photos to your bulletin (JPEG, PNG, GIF, WebP)"
        photos={formData.photos || []}
        onPhotosChange={(photos) => handleFieldChange('photos', photos)}
        onAppendPhotos={(photos) => appendPhotos(photos as any)}
        error={errors.photos}
        maxPhotos={10}
        photoType="bulletin"
      />

      <RecipientOptionsSection
        formData={formData}
        onFieldChange={handleFieldChange}
      />

      <UrgencyOptionsSection
        formData={formData}
        onFieldChange={handleFieldChange}
      />

      <PublishOptionsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onPublishTypeChange={handlePublishTypeChange}
        onPublishTimePress={handlePublishTimePress}
      />
    </>
  ), [
    formData,
    errors,
    handleFieldChange,
    handlePublishTypeChange,
    handlePublishTimePress,
  ]);

  if (layoutMode === 'desktop') {
    return (
      <>
        <DesktopFormPage
          title={bulletinTableRow.title || 'Edit Bulletin'}
          subtitle="Update bulletin details"
          isSubmitting={isSubmitting}
          submitText="Update"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        >
          {scrollContent}
        </DesktopFormPage>
        <BulletinDateTimePickers
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirm as any}
          onCancel={hideDatePickers}
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
        />
      </>
    );
  }

  return (
    <MobileFormScreen
      screenName="EditBulletin"
      navigation={navigation}
      isSubmitting={isSubmitting}
      submitText="Update"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    >
      {scrollContent}
      <DateTimePickerModal
        DateTimePicker={DateTimePicker}
        datePickerState={datePickerState}
        onDateChange={handleDatePickerChange}
        onConfirm={handleDatePickerConfirm}
        onCancel={hideDatePickers}
        pickerType="publish"
        title="Publish Time"
        onModeChange={setDatePickerMode}
        webAnchor={webAnchor}
      />
    </MobileFormScreen>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 20,
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100, // Space for action buttons
  },
});
